import type {ModulePermission} from './routeUtils'

/**
 * Bidirectional submodule key aliases used during override matching.
 * Role definitions may use `company_documents` while user overrides use
 * `documents` (or vice-versa). This map lets the matching step resolve
 * either variant to the other so overrides are never silently dropped.
 *
 * Keep in sync with PERMISSION_SUBMODULE_ALIASES in resolvePermission.ts.
 */
const SUBMODULE_KEY_EQUIVALENTS: Record<string, string[]> = {
  company_documents: ['documents'],
  documents: ['company_documents', 'agency_documents'],
  agency_documents: ['documents'],
}

const MANAGE_COMPANIES_KEY = 'manage_companies'

/**
 * Key mappings for legacy -> correct keys under 'manage_companies' submodules
 *
 * Legacy data issue: Some roles in the database have keys like 'company_region', 'company_positions',
 * etc. under 'manage_companies' submodules, but module-config defines them as 'region', 'positions'.
 * The predefined role configs (mspplatform predefined-roles.config.ts) still spell them so, while
 * every tree the user editors save uses the catalogue's spelling.
 *
 * Only applies to Platform, MSP, and Agency roles - Company roles use the company_ prefixed keys
 * correctly. normalizePermissionTreeKeys (useRolePermissions) and the editors' form state
 * (usePermissionsState's normalizeModulePermissionKeys) rename by it; findOverrideSub matches by it,
 * both ways. mspplatform's MANAGE_COMPANIES_LEGACY_KEYS (libs/common cap-user-permissions.ts) is the
 * same table.
 */
export const LEGACY_KEY_MAPPINGS: Record<string, string> = {
  company_region: 'region',
  company_positions: 'positions',
  company_orders: 'orders',
  company_documents: 'documents',
  company_invoices: 'invoices',
  company_timesheets: 'timesheets',
}

/**
 * STFFLG-4269: under Manage Companies only, each legacy spelling answers to the catalogue's and back
 * (`orders` and `documents` are other nodes elsewhere). Without it a Companies tab the Manage
 * permissions popup saved off (`region`) never reached the role's `company_region`, so the role's
 * grant survived and lit the Companies nav item.
 */
const MANAGE_COMPANIES_KEY_EQUIVALENTS: Record<string, string[]> = Object.fromEntries(
  Object.entries(LEGACY_KEY_MAPPINGS).flatMap(([legacy, catalogue]): [string, string[]][] => [
    [legacy, [catalogue]],
    [catalogue, [legacy]],
  ]),
)

/**
 * Find an override submodule that matches a role submodule key,
 * falling back to known key equivalents when an exact match fails.
 * Pass the key of the node both lists sit under (`parentKey`) to also match the Manage Companies
 * spellings (MANAGE_COMPANIES_KEY_EQUIVALENTS) beneath `manage_companies`.
 * The user editors' role cap (Roles_And_Permissions/utils/roleCap.ts) matches nodes with it too.
 */
export function findOverrideSub<T extends {key: string}>(
  overrideSubs: readonly T[],
  roleSubKey: string,
  parentKey?: string,
): T | undefined {
  const direct = overrideSubs.find((os) => os.key === roleSubKey)
  if (direct) return direct
  const scoped =
    parentKey === MANAGE_COMPANIES_KEY ? MANAGE_COMPANIES_KEY_EQUIVALENTS[roleSubKey] : []
  const equivalents = [...(scoped ?? []), ...(SUBMODULE_KEY_EQUIVALENTS[roleSubKey] ?? [])]
  for (const alias of equivalents) {
    const found = overrideSubs.find((os) => os.key === alias)
    if (found) return found
  }
  return undefined
}

function clonePermission(permission: ModulePermission): ModulePermission {
  return {
    ...permission,
    submodules: permission.submodules?.map(clonePermission) || [],
  }
}

/**
 * Applies AND logic between role permissions and user/agency overrides.
 * Overrides can only restrict access, never expand it.
 *
 * For each module present in both lists (matched by `key` at the same
 * position in the tree), every action flag is ANDed:
 *   effective = roleValue && overrideValue
 * A module's submodules are matched by findOverrideSub with the module's key, so under Manage
 * Companies an override in either spelling (`invoices` / `company_invoices`, …) restricts the
 * role's node in the other (STFFLG-4269: a Platform user's tree, saved by Add / Edit User in the
 * catalogue's spelling, is the only override such a user has).
 *
 * Modules present in rolePermissions but absent from overrides at the same
 * position are untouched. Handles recursive nesting
 * (submodules -> sub-submodules -> ...).
 *
 * Use for USER-LEVEL overrides which should only restrict, never escalate.
 * For agency-level overrides that may escalate, see applyAuthoritativeOverrides.
 *
 * IMPORTANT: This implementation is a 1:1 port of the backend's
 * `apply-restrictive-overrides.ts` in mspservice/libs/common/src/module-permission, which matches
 * submodules the same way (findEquivalentNode with the parent key). The backend applies it to the
 * agency assignments, whose card-ui counterpart is applyAuthoritativeOverrides (REPLACE, see
 * engineering-findings P4). Keep the two in sync so client and server produce identical permission
 * resolutions for the same input trees.
 */
export function applyRestrictiveOverrides(
  rolePermissions: ModulePermission[],
  overrides: ModulePermission[],
): ModulePermission[] {
  return rolePermissions.map((rolePerm) => {
    const override = overrides.find((o) => o.key === rolePerm.key)
    if (!override) {
      return rolePerm
    }

    // Wrap the ANDed flags in Boolean(...) so a missing (undefined) flag on the
    // override node coerces to `false` instead of leaking `undefined`. The
    // ModulePermission type declares these fields as `boolean`, so plain `&&`
    // with a `=== true` guard gets auto-fixed away by
    // @typescript-eslint/no-unnecessary-boolean-literal-compare; the explicit
    // Boolean(...) call survives that rule because it's a coercion call in
    // value position, not a literal comparison. See:
    // applyRestrictiveOverrides.test.ts > "Missing flags on override nodes".
    const merged: ModulePermission = {
      ...rolePerm,
      canView: Boolean(rolePerm.canView && override.canView),
      canCreate: Boolean(rolePerm.canCreate && override.canCreate),
      canEdit: Boolean(rolePerm.canEdit && override.canEdit),
      canDelete: Boolean(rolePerm.canDelete && override.canDelete),
    }

    if (rolePerm.submodules && rolePerm.submodules.length > 0) {
      merged.submodules = rolePerm.submodules.map((roleSub) => {
        const overrideSubs = override.submodules || []
        // A Companies tab matches in either spelling (MANAGE_COMPANIES_KEY_EQUIVALENTS)
        const overrideSub = findOverrideSub(overrideSubs, roleSub.key, rolePerm.key)
        if (!overrideSub) {
          return roleSub
        }
        const mergedSub: ModulePermission = {
          ...roleSub,
          canView: Boolean(roleSub.canView && overrideSub.canView),
          canCreate: Boolean(roleSub.canCreate && overrideSub.canCreate),
          canEdit: Boolean(roleSub.canEdit && overrideSub.canEdit),
          canDelete: Boolean(roleSub.canDelete && overrideSub.canDelete),
        }
        if (roleSub.submodules?.length) {
          mergedSub.submodules = applyRestrictiveOverrides(
            roleSub.submodules,
            overrideSub.submodules || [],
          )
        }
        return mergedSub
      })
    }

    return merged
  })
}

/**
 * Applies authoritative overrides: when an override exists for a module,
 * its values REPLACE the role defaults (allowing both escalation and
 * restriction). When no override exists, the role default is kept.
 * Override-only modules are appended so agency assignments can grant modules
 * absent from the base role. A module's submodules are matched by findOverrideSub, so under
 * Manage Companies an override in either spelling (`region` / `company_region`, …) replaces the
 * role's node in the other (STFFLG-4269); an override submodule the role has in neither spelling is
 * not added, as before.
 *
 * STFFLG-2281: Platform admins could set per-agency permissions that exceed
 * the base role's ceiling. The sign-in flow uses this function for the
 * agency override step so escalated values take effect at runtime.
 * STFFLG-4243: the user editors now save every per-agency tree capped by the
 * user's role, for every editor, so for a tree saved since then this REPLACE
 * can only restrict. A tree saved above the role before it still escalates
 * here until the backend caps the stored data.
 *
 * Use for AGENCY-LEVEL overrides only. User-level overrides should always
 * use applyRestrictiveOverrides (AND logic) to enforce the user-restriction
 * ceiling.
 *
 * TODO: The backend auth.guard.ts still uses AND logic for agency overrides
 * in checkModulePermissions. Update the backend to match this authoritative
 * semantic for full end-to-end escalation support.
 */
export function applyAuthoritativeOverrides(
  rolePermissions: ModulePermission[],
  overrides: ModulePermission[],
): ModulePermission[] {
  const mergedPermissions = rolePermissions.map((rolePerm) => {
    const override = overrides.find((o) => o.key === rolePerm.key)
    if (!override) {
      return rolePerm
    }

    const merged: ModulePermission = {
      ...rolePerm,
      canView: Boolean(override.canView),
      canCreate: Boolean(override.canCreate),
      canEdit: Boolean(override.canEdit),
      canDelete: Boolean(override.canDelete),
    }

    if (rolePerm.submodules && rolePerm.submodules.length > 0) {
      merged.submodules = rolePerm.submodules.map((roleSub) => {
        const overrideSubs = override.submodules || []
        // A Companies tab matches in either spelling (MANAGE_COMPANIES_KEY_EQUIVALENTS)
        const overrideSub = findOverrideSub(overrideSubs, roleSub.key, rolePerm.key)
        if (!overrideSub) {
          return roleSub
        }
        const mergedSub: ModulePermission = {
          ...roleSub,
          canView: Boolean(overrideSub.canView),
          canCreate: Boolean(overrideSub.canCreate),
          canEdit: Boolean(overrideSub.canEdit),
          canDelete: Boolean(overrideSub.canDelete),
        }
        if (roleSub.submodules?.length) {
          mergedSub.submodules = applyAuthoritativeOverrides(
            roleSub.submodules,
            overrideSub.submodules || [],
          )
        }
        return mergedSub
      })
    }

    return merged
  })

  const rolePermissionKeys = new Set(rolePermissions.map((permission) => permission.key))
  const overrideOnlyPermissions = overrides
    .filter((override) => !rolePermissionKeys.has(override.key))
    .map(clonePermission)

  return [...mergedPermissions, ...overrideOnlyPermissions]
}
